/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { ISbStoryData } from '@storyblok/react/rsc'
import { useAtomValue } from 'jotai'

import { MtaKitStoryblok } from '@/lib/storyblok/types/generated-types'
import { extractTextFromRichtext } from '@/lib/storyblok/utils/blog-post-extractor'
import { storyAtom } from '@/store/story'

/**
 * Component used to insert a script containing JSON-LD metadata to MTa Kit product pages. Pulls the Storyblok story
 * from local state and uses that to pull the respective information.
 */
const MtaKitPageSEO = () => {
  const story = useAtomValue(storyAtom) as ISbStoryData<MtaKitStoryblok> | null

  if (!story || story.content?.component !== 'mta-kit') {
    return null
  }

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: story.content.name,
    description: extractTextFromRichtext(story.content.description),
    image: story.content.logo.filename,
    sku: story.slug,
    brand: {
      '@type': 'Brand',
      name: 'MTa Learning',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: story.content.default_pricing[0].currency,
      price: story.content.default_pricing[0].amount,
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/${story.lang !== 'en' ? story.lang + '/' : ''}${story.full_slug}`,
    },
  }

  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      key='product-schema'
    />
  )
}

export { MtaKitPageSEO }
